import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Study } from 'interfaces/study';
import { map } from 'lodash';
import React from 'react';

interface LostAndFoundToolbarProps {
  canApplyCaseManifest: boolean;
  canIgnoreUploads: boolean;
  hasPendingSelection: boolean;
  hasSelectedUploadsWithoutSuccessfulIngest: boolean;
  onCreateCases: (groupIntoSingleCase: boolean) => void;
  onIgnoreUploads: () => void;
  onTargetStudyChange: (targetStudyId: string) => void;
  refreshRequired: boolean;
  selectedRowCount: number;
  studiesLoading: boolean;
  targetStudies: Study[];
  targetStudyId: string;
  visibleUploadCount: number;
}

export const LostAndFoundToolbar: React.FC<LostAndFoundToolbarProps> = ({
  canApplyCaseManifest,
  canIgnoreUploads,
  hasPendingSelection,
  hasSelectedUploadsWithoutSuccessfulIngest,
  onCreateCases,
  onIgnoreUploads,
  onTargetStudyChange,
  refreshRequired,
  selectedRowCount,
  studiesLoading,
  targetStudies,
  targetStudyId,
  visibleUploadCount,
}) => {
  const [caseActionsAnchorEl, setCaseActionsAnchorEl] = React.useState<HTMLElement | null>(null);
  const assignmentDisabled =
    refreshRequired ||
    !targetStudyId ||
    selectedRowCount === 0 ||
    hasPendingSelection ||
    hasSelectedUploadsWithoutSuccessfulIngest;
  const assignmentDisabledReason = refreshRequired
    ? 'Status changed. Refresh before organizing slides.'
    : hasSelectedUploadsWithoutSuccessfulIngest
    ? 'Case assignment is unavailable because one or more selected uploads have not ingested successfully.'
    : '';

  const handleCreateCases = (groupIntoSingleCase: boolean) => {
    setCaseActionsAnchorEl(null);
    onCreateCases(groupIntoSingleCase);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
      <Typography variant="caption" color="text.secondary">
        {visibleUploadCount} shown
      </Typography>
      {canApplyCaseManifest ? (
        <>
          <TextField
            select
            size="small"
            label="Target study"
            value={targetStudyId}
            onChange={(event) => onTargetStudyChange(event.target.value)}
            disabled={studiesLoading || targetStudies.length === 0}
            sx={{ width: 240, flex: '0 0 240px' }}
          >
            {map(targetStudies, (study) => (
              <MenuItem key={study.id} value={study.id}>
                {study.name}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={assignmentDisabledReason}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={assignmentDisabled}
                onClick={(event) => setCaseActionsAnchorEl(event.currentTarget)}
                endIcon={<ArrowDropDownIcon />}
              >
                Organize slides
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={caseActionsAnchorEl}
            open={Boolean(caseActionsAnchorEl)}
            onClose={() => setCaseActionsAnchorEl(null)}
          >
            <MenuItem onClick={() => handleCreateCases(false)}>One case per slide</MenuItem>
            <MenuItem onClick={() => handleCreateCases(true)}>Group into one case</MenuItem>
          </Menu>
        </>
      ) : null}
      {canIgnoreUploads ? (
        <Button
          size="small"
          color="warning"
          variant="contained"
          disabled={selectedRowCount === 0 || hasPendingSelection}
          onClick={onIgnoreUploads}
        >
          Ignore upload
        </Button>
      ) : null}
    </Stack>
  );
};
